'use client';

import { useMemo, useState } from 'react';
import {
  ATTACHMENT_BLURBS,
  ATTACHMENT_QUESTIONS,
  ATTACHMENT_STYLES,
  type AttachmentStyle,
} from '@/lib/quiz-data/attachment-questions';
import {
  MBTI_AXES,
  MBTI_POLE_LABEL,
  MBTI_QUESTIONS,
  MBTI_TYPE_BLURBS,
  type MbtiPole,
} from '@/lib/quiz-data/mbti-questions';
import {
  RIASEC_CATEGORIES,
  RIASEC_LABELS,
  RIASEC_QUESTIONS,
  type RiasecCategory,
} from '@/lib/quiz-data/riasec-questions';
import {
  ENNEAGRAM_BLURBS,
  ENNEAGRAM_QUESTIONS,
  ENNEAGRAM_TYPES,
  type EnneagramType,
} from '@/lib/quiz-data/enneagram-questions';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

export type PersonalityQuizVariant =
  | 'mbti-test'
  | 'attachment-style-quiz'
  | 'career-test'
  | 'enneagram-test';

const LIKERT: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Disagree' },
  { value: 2, label: 'Slightly disagree' },
  { value: 3, label: 'Neutral' },
  { value: 4, label: 'Slightly agree' },
  { value: 5, label: 'Agree' },
];

type MbtiResult = {
  kind: 'mbti';
  code: string;
  axisScores: Array<{ pole: MbtiPole; other: MbtiPole; score: number; otherScore: number }>;
  blurb: { name: string; blurb: string } | undefined;
};

type AttachmentResult = {
  kind: 'attachment';
  winner: AttachmentStyle;
  percentages: Record<AttachmentStyle, number>;
  blurb: { name: string; blurb: string };
};

type RiasecResult = {
  kind: 'riasec';
  code: string;
  topThree: RiasecCategory[];
  percentages: Record<RiasecCategory, number>;
};

type EnneagramResult = {
  kind: 'enneagram';
  winner: EnneagramType;
  wing: EnneagramType;
  percentages: Record<EnneagramType, number>;
};

function questionsFor(variant: PersonalityQuizVariant) {
  if (variant === 'enneagram-test') return ENNEAGRAM_QUESTIONS;
  if (variant === 'mbti-test') return MBTI_QUESTIONS;
  if (variant === 'career-test') return RIASEC_QUESTIONS;
  return ATTACHMENT_QUESTIONS;
}

export default function BrowserPersonalityQuiz({ variant }: { variant: PersonalityQuizVariant }) {
  const questions = questionsFor(variant);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submitted, setSubmitted] = useState(false);

  const answeredCount = Object.keys(answers).length;

  function setAnswer(id: string, value: number) {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  }

  function handleSubmit() {
    setSubmitted(true);
    // The real action is finishing the quiz, not loading the page.
    void consumeToolUsage(variant, '').catch(() => undefined);
  }

  function reset() {
    setAnswers({});
    setSubmitted(false);
  }

  const result = useMemo<
    MbtiResult | AttachmentResult | RiasecResult | EnneagramResult | null
  >(() => {
    if (!submitted) return null;

    if (variant === 'enneagram-test') {
      const scores = {} as Record<EnneagramType, number>;
      for (const t of ENNEAGRAM_TYPES) scores[t] = 0;
      for (const q of ENNEAGRAM_QUESTIONS) scores[q.type] += answers[q.id] ?? 3;
      const per = ENNEAGRAM_QUESTIONS.length / ENNEAGRAM_TYPES.length;
      const percentages = {} as Record<EnneagramType, number>;
      for (const t of ENNEAGRAM_TYPES) percentages[t] = Math.round((scores[t] / (per * 5)) * 100);
      const winner = ENNEAGRAM_TYPES.reduce((best, t) => (scores[t] > scores[best] ? t : best));
      // A wing is one of the two neighbouring types on the circle (9 wraps to 1).
      const left = (winner === 1 ? 9 : winner - 1) as EnneagramType;
      const right = (winner === 9 ? 1 : winner + 1) as EnneagramType;
      const wing = scores[left] >= scores[right] ? left : right;
      return { kind: 'enneagram', winner, wing, percentages };
    }

    if (variant === 'mbti-test') {
      const scores: Record<MbtiPole, number> = { E: 0, I: 0, S: 0, N: 0, T: 0, F: 0, J: 0, P: 0 };
      for (const q of MBTI_QUESTIONS) scores[q.pole] += answers[q.id] ?? 3;
      let code = '';
      const axisScores: MbtiResult['axisScores'] = [];
      for (const [a, b] of MBTI_AXES) {
        code += scores[a] >= scores[b] ? a : b;
        axisScores.push({ pole: a, other: b, score: scores[a], otherScore: scores[b] });
      }
      return { kind: 'mbti', code, axisScores, blurb: MBTI_TYPE_BLURBS[code] };
    }

    if (variant === 'career-test') {
      const scores: Record<RiasecCategory, number> = { R: 0, I: 0, A: 0, S: 0, E: 0, C: 0 };
      const counts: Record<RiasecCategory, number> = { R: 0, I: 0, A: 0, S: 0, E: 0, C: 0 };
      for (const q of RIASEC_QUESTIONS) {
        scores[q.category] += answers[q.id] ?? 3;
        counts[q.category] += 1;
      }
      const percentages = {} as Record<RiasecCategory, number>;
      for (const cat of RIASEC_CATEGORIES) {
        percentages[cat] = Math.round((scores[cat] / (counts[cat] * 5)) * 100);
      }
      const ranked = [...RIASEC_CATEGORIES].sort((a, b) => percentages[b] - percentages[a]);
      const topThree = ranked.slice(0, 3);
      return { kind: 'riasec', code: topThree.join(''), topThree, percentages };
    }

    const scores: Record<AttachmentStyle, number> = {
      secure: 0,
      anxious: 0,
      avoidant: 0,
      fearful: 0,
    };
    const counts: Record<AttachmentStyle, number> = {
      secure: 0,
      anxious: 0,
      avoidant: 0,
      fearful: 0,
    };
    for (const q of ATTACHMENT_QUESTIONS) {
      scores[q.style] += answers[q.id] ?? 3;
      counts[q.style] += 1;
    }
    const percentages = {} as Record<AttachmentStyle, number>;
    for (const style of ATTACHMENT_STYLES) {
      percentages[style] = Math.round((scores[style] / (counts[style] * 5)) * 100);
    }
    const winner = ATTACHMENT_STYLES.reduce((best, s) =>
      percentages[s] > percentages[best] ? s : best,
    );
    return { kind: 'attachment', winner, percentages, blurb: ATTACHMENT_BLURBS[winner] };
  }, [submitted, answers, variant]);

  const title =
    variant === 'enneagram-test'
      ? 'Enneagram test'
      : variant === 'mbti-test'
        ? '16-type personality test'
        : variant === 'career-test'
          ? 'Career test'
          : 'Attachment style quiz';
  const badge =
    variant === 'enneagram-test'
      ? 'Personality'
      : variant === 'mbti-test'
        ? 'Personality'
        : variant === 'career-test'
          ? 'Career'
          : 'Relationships';
  const subtitle =
    variant === 'enneagram-test'
      ? 'An informal, 36-question self-reflection test based on the nine Enneagram types. Nothing is sent anywhere; your answers stay in this tab.'
      : variant === 'mbti-test'
        ? 'An informal, 32-question self-reflection test based on the four-axis personality-type framework. Nothing is sent anywhere; your answers stay in this tab.'
        : variant === 'career-test'
          ? 'An informal, 30-question career-interest test based on the RIASEC (Holland Code) framework. Nothing is sent anywhere; your answers stay in this tab.'
          : 'An informal, 20-question self-reflection quiz based on adult attachment theory. Not a clinical assessment. Nothing is sent anywhere.';

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{badge}</span>
        <h2 className="tool-live-title">{title}</h2>
        <p className="tool-live-subtitle">{subtitle}</p>
      </div>

      {!submitted ? (
        <div className="quiz-panel">
          <p className="quiz-progress" aria-live="polite">
            {answeredCount} of {questions.length} answered
          </p>
          <div className="quiz-question-list">
            {questions.map((q, index) => (
              <fieldset className="quiz-question" key={q.id}>
                <legend>
                  {index + 1}. {q.text}
                </legend>
                <div className="quiz-likert" role="radiogroup">
                  {LIKERT.map((option) => (
                    <label className="tool-live-option" key={option.value}>
                      <input
                        checked={answers[q.id] === option.value}
                        name={q.id}
                        onChange={() => setAnswer(q.id, option.value)}
                        type="radio"
                        value={option.value}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </fieldset>
            ))}
          </div>
          <div className="tool-live-actions">
            <button className="button" onClick={handleSubmit} type="button">
              See my result
            </button>
          </div>
          {answeredCount < questions.length ? (
            <p className="tool-live-panel-note">
              Unanswered questions count as neutral. Answering all {questions.length} gives the most
              accurate result.
            </p>
          ) : null}
        </div>
      ) : (
        <div className="quiz-panel">
          {result?.kind === 'mbti' ? (
            <>
              <div className="quiz-result-headline">
                <span className="quiz-result-code">{result.code}</span>
                <span className="quiz-result-name">{result.blurb?.name}</span>
              </div>
              <p className="tool-live-panel-note">{result.blurb?.blurb}</p>
              <div className="quiz-axis-list">
                {result.axisScores.map((axis) => {
                  const total = axis.score + axis.otherScore;
                  const pct = total ? Math.round((axis.score / total) * 100) : 50;
                  return (
                    <div className="quiz-axis-row" key={axis.pole}>
                      <span className="quiz-axis-label">{MBTI_POLE_LABEL[axis.pole]}</span>
                      <div className="quiz-axis-bar">
                        <div className="quiz-axis-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="quiz-axis-label quiz-axis-label-right">
                        {MBTI_POLE_LABEL[axis.other]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}

          {result?.kind === 'enneagram' ? (
            <>
              <div className="quiz-result-headline">
                <span className="quiz-result-code">
                  {result.winner}w{result.wing}
                </span>
                <span className="quiz-result-name">{ENNEAGRAM_BLURBS[result.winner].name}</span>
              </div>
              <p className="tool-live-panel-note">{ENNEAGRAM_BLURBS[result.winner].blurb}</p>
              <p className="tool-live-panel-note">
                Your wing, type {result.wing} ({ENNEAGRAM_BLURBS[result.wing].name}), is the
                neighbouring type that scored higher and colours how your main type shows up.
              </p>
              <div className="quiz-style-list">
                {ENNEAGRAM_TYPES.map((t) => (
                  <div className="quiz-style-row" key={t}>
                    <span className="quiz-style-label">
                      {t}. {ENNEAGRAM_BLURBS[t].name}
                    </span>
                    <div className="quiz-axis-bar quiz-style-bar">
                      <div
                        className="quiz-axis-bar-fill"
                        style={{ width: `${result.percentages[t]}%` }}
                      />
                    </div>
                    <span className="quiz-style-pct">{result.percentages[t]}%</span>
                  </div>
                ))}
              </div>
              <p className="tool-live-panel-note">
                A short quiz is a starting point. If two types score close, read both descriptions:
                the Enneagram is about your core motivation, which you recognise better than any
                score does.
              </p>
            </>
          ) : null}

          {result?.kind === 'riasec' ? (
            <>
              <div className="quiz-result-headline">
                <span className="quiz-result-code">{result.code}</span>
              </div>
              <div className="quiz-axis-list">
                {result.topThree.map((cat) => (
                  <p className="tool-live-panel-note" key={cat}>
                    <strong>{RIASEC_LABELS[cat].name}</strong>
                    <span>{RIASEC_LABELS[cat].blurb}</span>
                  </p>
                ))}
              </div>
              <div className="quiz-style-list">
                {RIASEC_CATEGORIES.map((cat) => (
                  <div className="quiz-style-row" key={cat}>
                    <span className="quiz-style-label">{RIASEC_LABELS[cat].name}</span>
                    <div className="quiz-axis-bar quiz-style-bar">
                      <div
                        className="quiz-axis-bar-fill"
                        style={{ width: `${result.percentages[cat]}%` }}
                      />
                    </div>
                    <span className="quiz-style-pct">{result.percentages[cat]}%</span>
                  </div>
                ))}
              </div>
              <p className="tool-live-panel-note">
                Your 3-letter Holland Code is the 3 highest-scoring categories, in order. Most real
                careers blend more than one; use it as a starting point for exploring options, not a
                final answer.
              </p>
            </>
          ) : null}

          {result?.kind === 'attachment' ? (
            <>
              <div className="quiz-result-headline">
                <span className="quiz-result-name quiz-result-name-lg">{result.blurb.name}</span>
              </div>
              <p className="tool-live-panel-note">{result.blurb.blurb}</p>
              <div className="quiz-style-list">
                {ATTACHMENT_STYLES.map((style) => (
                  <div className="quiz-style-row" key={style}>
                    <span className="quiz-style-label">{ATTACHMENT_BLURBS[style].name}</span>
                    <div className="quiz-axis-bar quiz-style-bar">
                      <div
                        className="quiz-axis-bar-fill"
                        style={{ width: `${result.percentages[style]}%` }}
                      />
                    </div>
                    <span className="quiz-style-pct">{result.percentages[style]}%</span>
                  </div>
                ))}
              </div>
              <p className="tool-live-panel-note">
                This is an informal self-reflection tool, not a clinical diagnosis. If relationship
                patterns are causing real distress, a licensed therapist can help far more than any
                online quiz.
              </p>
            </>
          ) : null}

          <div className="tool-live-actions">
            <button className="button button-ghost" onClick={reset} type="button">
              Retake the quiz
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
